// Logitech "classic" force feedback protocol for the G27 (and its siblings the
// G25 and Driving Force GT), as used by the Linux kernel driver hid-lg4ff and
// the out-of-tree new-lg4ff driver. Every command is a 7-byte output report
// with no report ID.
//
// Force commands address one of four effect slots: the high nibble of byte 0
// is the slot mask (0x10, 0x20, 0x40, 0x80) and the low nibble the operation
// (0x1 download and play, 0x3 stop, 0xc refresh). Byte 1 is the effect type.
//
// The pure functions here only build bytes; G27Device sends them.

export const LOGITECH_VENDOR_ID = 0x046d;

export const PRODUCT = {
  /** Native mode G27: 900°, separate pedal axes, shifter, 5 rev LEDs. */
  G27: 0xc29b,
  G25: 0xc299,
  DFGT: 0xc29a,
  /** G29 with its mode switch on PS3 (the PC mode): hid-lg4ff drives it with the G27 command set. */
  G29: 0xc24f,
  /**
   * G29 with its switch on PS4: a different protocol that hid-lg4ff doesn't
   * drive either. Offered in the chooser only so the game can say to flip the switch.
   */
  G29_PS4: 0xc260,
  /**
   * Compatibility mode ("Driving Force EX"): what every Logitech wheel looks like
   * until a driver switches it to native mode. Linux's hid-logitech does that at
   * plug-in, so on Linux the browser normally sees the native ID.
   */
  COMPAT: 0xc294,
} as const;

/** Wheels this code drives (same command set for range, forces and LEDs). */
export const NATIVE_WHEELS: ReadonlySet<number> = new Set([PRODUCT.G27, PRODUCT.G25, PRODUCT.DFGT, PRODUCT.G29]);

/** Every wheel we'll offer in the chooser (not Logitech mice, keyboards or receivers). */
export const WHEEL_PRODUCTS: readonly number[] = [...NATIVE_WHEELS, PRODUCT.COMPAT, PRODUCT.G29_PS4];

export function isWheel(device: { vendorId: number; productId: number }): boolean {
  return device.vendorId === LOGITECH_VENDOR_ID && WHEEL_PRODUCTS.includes(device.productId);
}

export function wheelName(productId: number): string {
  switch (productId) {
    case PRODUCT.G27:
      return 'Logitech G27';
    case PRODUCT.G25:
      return 'Logitech G25';
    case PRODUCT.DFGT:
      return 'Logitech Driving Force GT';
    case PRODUCT.G29:
      return 'Logitech G29';
    case PRODUCT.G29_PS4:
      return 'Logitech G29 (PS4 mode)';
    case PRODUCT.COMPAT:
      return 'Logitech wheel (compatibility mode)';
    default:
      return `Logitech device ${productId.toString(16)}`;
  }
}

export type Report = Uint8Array<ArrayBuffer>;

function report(...bytes: number[]): Report {
  const r = new Uint8Array(7);
  r.set(bytes.map((b) => b & 0xff));
  return r;
}

/** Rotation range limits the G25/G27/DFGT accept, degrees lock to lock. */
export const MIN_RANGE = 40;
export const MAX_RANGE = 900;

/** Set the rotation range (hid-lg4ff lg4ff_set_range_g25). The wheel enforces it with end stops. */
export function setRange(degrees: number): Report {
  const r = Math.round(Math.min(MAX_RANGE, Math.max(MIN_RANGE, degrees)));
  return report(0xf8, 0x81, r & 0xff, r >> 8);
}

/** Wheels that can be switched out of compatibility mode with hid-lg4ff's EXT_CMD9. */
export type NativeModel = 'G27' | 'G29' | 'DFGT';

/**
 * Switch a wheel in compatibility mode to its native mode (hid-lg4ff
 * lg4ff_mode_switch_ext09_*); it detaches and comes back with its own product ID.
 * The command names the mode, so a G29 sent the G27 one would come back as a G27.
 */
export function nativeMode(model: NativeModel): Report[] {
  const mode = { DFGT: [0x03, 0x01], G27: [0x04, 0x01], G29: [0x05, 0x01, 0x01] }[model];
  return [
    report(0xf8, 0x0a), // revert to compatibility mode on USB reset
    report(0xf8, 0x09, ...mode), // switch mode, with detach
  ];
}

/**
 * Which wheel a compatibility-mode device is, from its USB product name. Every
 * Logitech wheel shares the compatibility-mode product ID, and WebHID doesn't
 * expose the firmware revision the kernel uses to tell them apart.
 */
export function modelFromName(name: string | undefined): NativeModel | undefined {
  if (!name) return undefined;
  if (/G29/i.test(name)) return 'G29';
  if (/G27/i.test(name)) return 'G27';
  if (/Driving Force GT|DFGT/i.test(name)) return 'DFGT';
  return undefined;
}

/** Turn off the wheel's built-in centring spring (the game supplies its own forces). */
export function autocenterOff(): Report {
  return report(0xf5);
}

/**
 * The built-in centring spring at `strength` 0..1 (hid-lg4ff
 * lg4ff_set_autocenter_default). Used when the game isn't driving, so the
 * wheel doesn't flop around in menus.
 */
export function autocenter(strength: number): Report[] {
  const magnitude = Math.round(Math.min(1, Math.max(0, strength)) * 0xffff);
  if (magnitude === 0) return [autocenterOff()];
  let expandA: number;
  let expandB: number;
  if (magnitude <= 0xaaaa) {
    expandA = 0x0c * magnitude;
    expandB = 0x80 * magnitude;
  } else {
    expandA = 0x0c * 0xaaaa + 0x06 * (magnitude - 0xaaaa);
    expandB = 0x80 * 0xaaaa + 0xff * (magnitude - 0xaaaa);
  }
  // Non-MOMO wheels take half the spring coefficient.
  expandA >>= 1;
  const k = Math.floor(expandA / 0xaaaa);
  return [report(0xfe, 0x0d, k, k, Math.floor(expandB / 0xaaaa)), report(0x14)];
}

/** Convert a force in −1..1 to the protocol's unsigned byte, 0x80 = no force. */
export function forceByte(level: number): number {
  const l = Math.min(1, Math.max(-1, Number.isFinite(level) ? level : 0));
  return Math.min(255, Math.max(0, Math.round(0x80 + l * 127)));
}

/**
 * Constant force in slot 1, −1..1, exactly as hid-lg4ff plays FF_CONSTANT
 * (type 0x08 "variable" with a zero step, which holds the level). Positive
 * values are bytes above 0x80; which way that turns the wheel is calibrated
 * at runtime (WheelProfile.forceSign), not assumed.
 */
export function constantForce(level: number): Report {
  const x = forceByte(level);
  if (x === 0x80) return stopSlot(0);
  return report(0x11, 0x08, x, 0x80);
}

export function stopSlot(slot: 0 | 1 | 2 | 3): Report {
  return report((0x10 << slot) | 0x03);
}

/** Stop every slot's force. */
export function stopAll(): Report {
  return report(0xf3);
}

/**
 * High-resolution damper (type 0x0c) in `slot`: resists wheel speed, computed
 * by the wheel's own controller at its internal rate, so it stays stable even
 * though the browser can only update forces at ~250 Hz. `k` 0..1.
 */
export function damper(slot: 1 | 2 | 3, k: number): Report {
  const coeff = Math.round(Math.min(1, Math.max(0, k)) * 15);
  if (coeff === 0) return stopSlot(slot);
  return report((0x10 << slot) | 0x01, 0x0c, coeff, 0, coeff, 0, 0xff);
}

/** Friction (type 0x0e) in `slot`: a constant drag against motion. `k` 0..1. */
export function friction(slot: 1 | 2 | 3, k: number): Report {
  const coeff = Math.round(Math.min(1, Math.max(0, k)) * 255);
  if (coeff === 0) return stopSlot(slot);
  return report((0x10 << slot) | 0x01, 0x0e, coeff, coeff, 0xff, 0, 0);
}

/** The 5 rev LEDs on the G27 rim, as a bitmask (bit 0 = first LED). */
export function setLeds(mask: number): Report {
  return report(0xf8, 0x12, mask & 0x1f);
}

/** LED bitmask for a shift-light level 0..1; flashes all LEDs past `flashFrom` when `flashOn`. */
export function ledMask(level: number, flashOn: boolean, flashFrom = 0.97): number {
  if (level >= flashFrom) return flashOn ? 0x1f : 0;
  const lit = Math.max(0, Math.min(5, Math.ceil(level * 5 - 1e-9)));
  return (1 << lit) - 1;
}

export function toHex(r: Uint8Array): string {
  return [...r].map((b) => b.toString(16).padStart(2, '0')).join(' ');
}
